import { Heading, Text } from "@sentricmusic/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { parse } from "qs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSignupConfirm } from "@/api/mutations";
import AuthPrimaryButton from "@/components/AuthPrimaryButton";
import FormError from "@/components/FormError";
import Input from "@/components/Input";
import Spacer from "@/components/Spacer";

function SignUpConfirm() {
  const { t } = useTranslation();
  const [warnings, setWarnings] = useState({});
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [valid, setValid] = useState(false);
  const [pending, setPending] = useState(false);

  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const navigate = useNavigate();
  const locationSearch = useRouterState({ select: s => s.location.searchStr });
  const usernameRegex = /^(?=[\w.-]{6,24}$)(?!.*[_.-]{2})[^_.-].*[^_.-]$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&+=!£*]).{8,100}$/;
  const signUpConfirmMutation = useSignupConfirm();

  const links = (parse(locationSearch.slice(1)));

  const confirm = async () => {
    setValid(false);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    setPending(true);
    const data = {
      linktoken: links.lt,
      linkcontext: links.lc,
      username,
      password,
    };
    signUpConfirmMutation.reset();
    signUpConfirmMutation.mutate(data, {
      onSuccess: (response) => {
        const message = response.messages && response.messages.length > 0 ? response.messages[0] : "Unknown error";
        if (message !== "Success.") {
          setValid(true);
          setValidationErrorMessage(response.messages);
          setPending(false);
        }
        else {
          // Account created successfully - redirect to login
          setPending(false);
          navigate({ to: "/login" });
        }
      },
      onError: (error) => {
        setValid(true);
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const checkValid = (passed = {}) => {
    let val = true;

    // firstname check
    const name = passed.username || username;
    const validUsername = name.trim().length > 5;
    if (!validUsername) {
      val = false;
    }

    // password check
    const pass = passed.password || password;
    const validPassword = pass.length > 6 && pass.length < 100;

    if (!validPassword) {
      val = false;
    }

    // pass regex check
    if (!passwordRegex.test(pass)) {
      val = false;
    }

    setValid(val);
  };

  const checkErrors = () => {
    const errs = {};

    // first name check
    if (username.trim().length > 0) {
      const validUsername = username.trim().length > 5;
      if (!validUsername) {
        errs.username = t("auth:longer-username-error");
      }
    }

    // last name check
    if (password.trim().length > 6) {
      let validPassword = password.trim().length > 6;
      validPassword = password.trim().length < 100;
      if (!validPassword) {
        errs.password = t("regex:password-regex-error2");
      }
    }

    // user regex check
    if (!usernameRegex.test(username)) {
      errs.username = t("regex:username-regex");
    }

    // pass regex check
    if (!passwordRegex.test(password)) {
      errs.password = t("regex:password-regex-error");
    }
    setWarnings(errs);
  };

  return (
    <div>
      <div className="container mx-auto">
        <div className="flex flex-col items-center justify-center p-4 text-center min-h-screen-minus-footer">
          <Heading as="h1" className="text-white">
            {t("auth:account-setup-header")}
          </Heading>
          <Spacer />
          <Text as="bodyLg" className="text-white">{t("auth:account-setup-text")}</Text>
          <div className="w-full lg:w-1/3 text-ink-primary">
            <Spacer />
            <Input
              error={warnings.username}
              className="auth-page-input"
              placeholder={t("auth:username-placeholder")}
              value={username}
              onBlur={() => checkErrors()}
              onChange={(e) => {
                setUsername(e.target.value);
                checkValid({ username: e.target.value });
              }}
              whiteLabelText
            />
            <Spacer />
            <Input
              className="auth-page-input"
              error={warnings.password}
              type="password"
              placeholder={t("auth:password-placeholder")}
              onBlur={() => checkErrors()}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                checkValid({ password: e.target.value });
              }}
              whiteLabelText
            />
            <Spacer />
            <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
            <AuthPrimaryButton
              id="btnLogin"
              disabled={!valid}
              pending={pending}
              onClick={confirm}
            >
              {t("common:next-button")}
            </AuthPrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpConfirm;
